'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { trackEvent } from '@/lib/analytics';

interface BRSRFormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  country: string;
  partnerType: 'corporate' | 'sme' | 'government' | 'ngo' | '';
  complianceFramework: 'brsr' | 'csrd' | 'ghg-protocol' | 'other' | '';
  sector: string;
  complianceStatus: 'not-started' | 'in-progress' | 'completed' | 'needs-improvement' | '';
  message: string;
}

interface BRSRInquiryFormProps {
  currentLanguage: string;
}

export default function BRSRInquiryForm({ currentLanguage }: BRSRInquiryFormProps) {
  const [formData, setFormData] = useState<BRSRFormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    country: 'india',
    partnerType: '',
    complianceFramework: '',
    sector: '',
    complianceStatus: '',
    message: '',
  });

  const [errors, setErrors] = useState<Partial<BRSRFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [routingInfo, setRoutingInfo] = useState<any>(null);

  const content = {
    en: {
      title: 'BRSR/Net Zero Consultation Inquiry',
      subtitle: 'Get expert guidance for empanelment and compliance',
      fullName: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      company: 'Company/Organization',
      country: 'Country/Region',
      partnerType: 'Partner Type',
      complianceFramework: 'Compliance Framework',
      sector: 'Industry Sector',
      complianceStatus: 'Current Compliance Status',
      message: 'Additional Requirements',
      submit: 'Submit Inquiry',
      submitting: 'Submitting...',
      successTitle: 'Inquiry Submitted Successfully!',
      successMessage: 'Your inquiry has been routed to',
      estimatedResponse: 'Estimated response time',
      inquiryId: 'Inquiry ID',
      submitAnother: 'Submit Another Inquiry',
      required: '*',
    },
    hi: {
      title: 'BRSR/नेट ज़ीरो परामर्श पूछताछ',
      subtitle: 'सूचीबद्धता और अनुपालन के लिए विशेषज्ञ मार्गदर्शन प्राप्त करें',
      fullName: 'पूरा नाम',
      email: 'ईमेल पता',
      phone: 'फ़ोन नंबर',
      company: 'कंपनी/संगठन',
      country: 'देश/क्षेत्र',
      partnerType: 'साझेदार प्रकार',
      complianceFramework: 'अनुपालन ढांचा',
      sector: 'उद्योग क्षेत्र',
      complianceStatus: 'वर्तमान अनुपालन स्थिति',
      message: 'अतिरिक्त आवश्यकताएं',
      submit: 'पूछताछ सबमिट करें',
      submitting: 'सबमिट हो रहा है...',
      successTitle: 'पूछताछ सफलतापूर्वक सबमिट की गई!',
      successMessage: 'आपकी पूछताछ को भेजा गया है',
      estimatedResponse: 'अनुमानित प्रतिक्रिया समय',
      inquiryId: 'पूछताछ आईडी',
      submitAnother: 'एक और पूछताछ सबमिट करें',
      required: '*',
    },
    ar: {
      title: 'استفسار استشارة BRSR/صافي الصفر',
      subtitle: 'احصل على إرشادات الخبراء للتسجيل والامتثال',
      fullName: 'الاسم الكامل',
      email: 'عنوان البريد الإلكتروني',
      phone: 'رقم الهاتف',
      company: 'الشركة/المنظمة',
      country: 'البلد/المنطقة',
      partnerType: 'نوع الشريك',
      complianceFramework: 'إطار الامتثال',
      sector: 'قطاع الصناعة',
      complianceStatus: 'حالة الامتثال الحالية',
      message: 'المتطلبات الإضافية',
      submit: 'إرسال الاستفسار',
      submitting: 'جارٍ الإرسال...',
      successTitle: 'تم إرسال الاستفسار بنجاح!',
      successMessage: 'تم توجيه استفسارك إلى',
      estimatedResponse: 'وقت الاستجابة المقدر',
      inquiryId: 'معرف الاستفسار',
      submitAnother: 'إرسال استفسار آخر',
      required: '*',
    },
    te: {
      title: 'BRSR/నెట్ జీరో సంప్రదింపు విచారణ',
      subtitle: 'ఎంపానెల్మెంట్ మరియు సమ్మతి కోసం నిపుణుల మార్గదర్శకత్వం పొందండి',
      fullName: 'పూర్తి పేరు',
      email: 'ఇమెయిల్ చిరునామా',
      phone: 'ఫోన్ నంబర్',
      company: 'కంపెనీ/సంస్థ',
      country: 'దేశం/ప్రాంతం',
      partnerType: 'భాగస్వామి రకం',
      complianceFramework: 'సమ్మతి ఫ్రేమ్‌వర్క్',
      sector: 'పరిశ్రమ రంగం',
      complianceStatus: 'ప్రస్తుత సమ్మతి స్థితి',
      message: 'అదనపు అవసరాలు',
      submit: 'విచారణ సమర్పించండి',
      submitting: 'సమర్పిస్తోంది...',
      successTitle: 'విచారణ విజయవంతంగా సమర్పించబడింది!',
      successMessage: 'మీ విచారణ దీనికి మార్గనిర్దేశం చేయబడింది',
      estimatedResponse: 'అంచనా ప్రతిస్పందన సమయం',
      inquiryId: 'విచారణ ID',
      submitAnother: 'మరొక విచారణ సమర్పించండి',
      required: '*',
    },
  };

  const lang = content[currentLanguage as keyof typeof content] || content.en;

  const partnerTypes = [
    { value: 'corporate', label: 'Corporate/Enterprise' },
    { value: 'sme', label: 'SME/Small Business' },
    { value: 'government', label: 'Government Entity' },
    { value: 'ngo', label: 'NGO/Non-Profit' },
  ];

  const complianceFrameworks = [
    { value: 'brsr', label: 'BRSR (India)' },
    { value: 'csrd', label: 'CSRD (Europe)' },
    { value: 'ghg-protocol', label: 'GHG Protocol' },
    { value: 'other', label: 'Other Framework' },
  ];

  const complianceStatuses = [
    { value: 'not-started', label: 'Not Started' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'needs-improvement', label: 'Needs Improvement' },
  ];

  const validateForm = (): boolean => {
    const newErrors: Partial<BRSRFormData> = {};

    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.partnerType) newErrors.partnerType = 'Partner type is required';
    if (!formData.complianceFramework) newErrors.complianceFramework = 'Compliance framework is required';
    if (!formData.sector.trim()) newErrors.sector = 'Industry sector is required';
    if (!formData.complianceStatus) newErrors.complianceStatus = 'Compliance status is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/brsr-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitSuccess(true);
        setRoutingInfo(data);
        trackEvent('brsr_inquiry_submitted', {
          partner_type: formData.partnerType,
          compliance_framework: formData.complianceFramework,
          compliance_status: formData.complianceStatus,
        });
      } else {
        alert(data.error || 'Failed to submit inquiry');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof BRSRFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      company: '',
      country: 'india',
      partnerType: '',
      complianceFramework: '',
      sector: '',
      complianceStatus: '',
      message: '',
    });
    setSubmitSuccess(false);
    setRoutingInfo(null);
  };

  if (submitSuccess && routingInfo) {
    return (
      <div className="bg-surface rounded-2xl p-8 border border-border">
        <div className="text-center">
          <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircleIcon" size={32} className="text-success" />
          </div>
          <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
            {lang.successTitle}
          </h3>
          <p className="text-text-secondary mb-6">
            {lang.successMessage}: <strong>{routingInfo.routing.consultant}</strong>
          </p>
          <div className="bg-card rounded-lg p-6 mb-6 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">{lang.inquiryId}:</span>
              <span className="font-mono text-text-primary">{routingInfo.inquiryId}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">{lang.estimatedResponse}:</span>
              <span className="text-text-primary font-semibold">
                {routingInfo.routing.estimatedResponseTime}
              </span>
            </div>
          </div>
          <button
            onClick={resetForm}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all duration-300"
          >
            {lang.submitAnother}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-2xl p-8 border border-border">
      <div className="mb-6">
        <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">{lang.title}</h3>
        <p className="text-text-secondary">{lang.subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-text-primary mb-2">
              {lang.fullName} <span className="text-error">{lang.required}</span>
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.fullName ? 'border-error' : 'border-input'
              } bg-card text-text-primary focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200`}
            />
            {errors.fullName && (
              <p className="mt-1 text-sm text-error flex items-center">
                <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                {errors.fullName}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
              {lang.email} <span className="text-error">{lang.required}</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.email ? 'border-error' : 'border-input'
              } bg-card text-text-primary focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-error flex items-center">
                <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                {errors.email}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-text-primary mb-2">
              {lang.phone} <span className="text-error">{lang.required}</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.phone ? 'border-error' : 'border-input'
              } bg-card text-text-primary focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200`}
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-error flex items-center">
                <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                {errors.phone}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-medium text-text-primary mb-2">
              {lang.company}
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-input bg-card text-text-primary focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200"
            />
          </div>

          <div>
            <label htmlFor="partnerType" className="block text-sm font-medium text-text-primary mb-2">
              {lang.partnerType} <span className="text-error">{lang.required}</span>
            </label>
            <select
              id="partnerType"
              name="partnerType"
              value={formData.partnerType}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.partnerType ? 'border-error' : 'border-input'
              } bg-card text-text-primary focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200`}
            >
              <option value="">Select partner type</option>
              {partnerTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.partnerType && (
              <p className="mt-1 text-sm text-error flex items-center">
                <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                {errors.partnerType}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="complianceFramework"
              className="block text-sm font-medium text-text-primary mb-2"
            >
              {lang.complianceFramework} <span className="text-error">{lang.required}</span>
            </label>
            <select
              id="complianceFramework"
              name="complianceFramework"
              value={formData.complianceFramework}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.complianceFramework ? 'border-error' : 'border-input'
              } bg-card text-text-primary focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200`}
            >
              <option value="">Select framework</option>
              {complianceFrameworks.map((framework) => (
                <option key={framework.value} value={framework.value}>
                  {framework.label}
                </option>
              ))}
            </select>
            {errors.complianceFramework && (
              <p className="mt-1 text-sm text-error flex items-center">
                <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                {errors.complianceFramework}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="sector" className="block text-sm font-medium text-text-primary mb-2">
              {lang.sector} <span className="text-error">{lang.required}</span>
            </label>
            <input
              type="text"
              id="sector"
              name="sector"
              value={formData.sector}
              onChange={handleChange}
              placeholder="e.g., Manufacturing, IT, Healthcare"
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.sector ? 'border-error' : 'border-input'
              } bg-card text-text-primary focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200`}
            />
            {errors.sector && (
              <p className="mt-1 text-sm text-error flex items-center">
                <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                {errors.sector}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="complianceStatus"
              className="block text-sm font-medium text-text-primary mb-2"
            >
              {lang.complianceStatus} <span className="text-error">{lang.required}</span>
            </label>
            <select
              id="complianceStatus"
              name="complianceStatus"
              value={formData.complianceStatus}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.complianceStatus ? 'border-error' : 'border-input'
              } bg-card text-text-primary focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200`}
            >
              <option value="">Select status</option>
              {complianceStatuses.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            {errors.complianceStatus && (
              <p className="mt-1 text-sm text-error flex items-center">
                <Icon name="ExclamationCircleIcon" size={16} className="mr-1" />
                {errors.complianceStatus}
              </p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-text-primary mb-2">
            {lang.message}
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-3 rounded-lg border border-input bg-card text-text-primary focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200 resize-none"
            placeholder="Please describe your specific requirements or questions..."
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full px-8 py-4 bg-conversion text-conversion-foreground font-heading font-semibold text-base rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic transform hover:scale-102 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span>{isSubmitting ? lang.submitting : lang.submit}</span>
          {!isSubmitting && <Icon name="ArrowRightIcon" size={20} />}
        </button>
      </form>
    </div>
  );
}
